import { type SupportedCountryCode } from '@zep-us/i18n';
import { type TFunction } from 'i18next';

import { getSafeSchoolNameTKey } from '@/shared/category/getSafeSchoolNameTKey';
import { SchoolMapper } from '@/shared/category/util';
import { type Namespace } from '@/types/translation';
import { type SchoolType } from '@/views/build/type';
import { getGradeNumber, isAllGrades } from '@/views/build/util';

import { type CategoryNode } from '../../category-tree';
import { type PublisherInfo } from '../../publisher-category-selector/types';

const COMMON_GRADE_ID = 10000;

/**
 * Pure function: Generate school type label from school name
 *
 * @param schoolName - School type name (e.g., "elementary", "middle")
 * @param t - Translation function
 * @returns Translated school type label (e.g., "초등학교")
 */
export function generateSchoolTypeLabel(
  schoolName: string,
  t: TFunction<Namespace>,
): string {
  return t(getSafeSchoolNameTKey(schoolName));
}

/**
 * Pure function: Generate grade label
 *
 * @param gradeId - Grade ID
 * @param userCountryCode - User's country code
 * @param t - Translation function
 * @returns Grade label (e.g., " 1-" for KR, " 1학년" for other countries)
 */
export function generateGradeLabel(
  gradeId: string | undefined,
  userCountryCode: SupportedCountryCode,
  t: TFunction<Namespace>,
): string | undefined {
  if (!gradeId || Number(gradeId) === COMMON_GRADE_ID) return undefined;

  const gradeNumber = getGradeNumber(gradeId);

  if (!gradeNumber) return undefined;

  if (isAllGrades(gradeNumber)) {
    return String(t('school.info.allGrades', { ns: 'common' }));
  }

  const shouldShort = userCountryCode === 'KR';

  if (shouldShort) {
    return `${gradeNumber}-`;
  } else {
    return `${t('school.info.nGrade', { n: gradeNumber, ns: 'common' })}`;
  }
}

/**
 * Pure function: Generate semester label
 *
 * @param semesterId - Semester ID
 * @param userCountryCode - User's country code
 * @param t - Translation function
 * @returns Semester label (e.g., "1" for KR, " 1학기" for other countries)
 */
export function generateSemesterLabel(
  semesterId: string | undefined,
  userCountryCode: SupportedCountryCode,
  t: TFunction<Namespace>,
): string | undefined {
  if (!semesterId) return undefined;

  const semesterNumber = Number(semesterId);
  const shouldShort = userCountryCode === 'KR';

  if (SchoolMapper.isEtc(semesterNumber)) {
    return t('school.info.shared', {
      ns: 'common',
    });
  } else if (shouldShort) {
    return semesterId;
  } else {
    return `${t('school.info.nSemester', {
      ns: 'common',
      n: semesterId,
    })}`;
  }
}

/**
 * 학교급, 학년, 학기 조합 label
 */
export function combineSchooInfoLabels({
  schoolTypeLabel,
  gradeLabel,
  semesterLabel,
}: {
  schoolTypeLabel?: string;
  gradeLabel?: string;
  semesterLabel?: string;
}) {
  const SPACE = ' ';

  if (!gradeLabel) return `${schoolTypeLabel || ''}`;

  if (gradeLabel?.endsWith('-')) {
    return `${schoolTypeLabel || ''}${SPACE}${gradeLabel || ''}${
      semesterLabel || ''
    }`;
  }
  return `${schoolTypeLabel || ''}${SPACE}${gradeLabel || ''}${SPACE}${
    semesterLabel || ''
  }`;
}

export function generateSubjectLabel({
  subjectId,
  subSubjectId,
  subjectNodes,
}: {
  subjectId: string | undefined;
  subSubjectId: string | undefined;
  subjectNodes: CategoryNode[];
}) {
  if (!subjectId) return undefined;

  const subjectNode = subjectNodes.find(node => node.nodeId === subjectId);
  if (!subjectNode) return undefined;

  if (subSubjectId) {
    const subSubjectNode = subjectNode.subNodes.find(
      node => node.nodeId === subSubjectId,
    );
    if (subSubjectNode) {
      return subSubjectNode.label;
    }
  }

  return subjectNode.label;
}

export function generatePublisherLabel({
  publisherId,
  customPublisher,
  publishers,
  userCountryCode,
  schoolTypeId,
  gradeId,
}: {
  publisherId: string | undefined;
  customPublisher: string | undefined;
  publishers?: PublisherInfo[];
  userCountryCode: SupportedCountryCode;
  schoolTypeId: string | undefined;
  gradeId: string | undefined;
}) {
  if (customPublisher) {
    return customPublisher;
  }

  if (!publisherId || !publishers) return undefined;

  // publishers 배열에서 직접 찾기
  const publisher = publishers.find(pub => pub.value === publisherId);
  if (!publisher) return undefined;

  // 한국의 경우 학교급과 학년 필터링 검증
  if (userCountryCode === 'KR' && schoolTypeId && gradeId) {
    const schoolGroupId = String(Number(schoolTypeId) % 100);
    const schoolType = SchoolMapper.getSchoolTypeNameById(
      Number(schoolGroupId),
    );
    const gradeNumber = getGradeNumber(gradeId);

    // 학교급에 따른 학년 오프셋 계산
    let gradeOffset = 0;
    if (schoolType === 'middle') {
      gradeOffset = 6;
    } else if (schoolType === 'high') {
      gradeOffset = 9;
    }
    const absoluteGrade = gradeNumber + gradeOffset;

    // 필터링 조건 검증
    const isValidSchoolType = publisher.showSchoolTypes?.includes(
      schoolType as SchoolType,
    );
    const isValidGrade = !publisher.hideGrades.includes(absoluteGrade || -1);

    if (isValidSchoolType && isValidGrade) {
      return publisher.label;
    }

    return undefined; // 필터링 조건 불만족
  }

  // 한국이 아닌 경우 또는 학교급/학년 정보 없는 경우
  return publisher.label;
}
